import { useRef, useState, type ClipboardEvent, type ReactElement } from 'react';
import {
  Link2,
  MessageSquareQuote,
  MoreHorizontal,
  PlugZap,
  UserCheck,
  X,
} from 'lucide-react';
import {
  ACTIVITY_VIA_TEMPLATE,
  COMMENT_BODY_MAX_LENGTH,
  type BoardMemberWithHistory,
  commentAnchor,
  type Comment,
  type CommentStreamComment,
  type ReactionEmoji,
} from '@molt/shared';

import { Avatar, profileName } from '@/components/Avatar';
import { Button } from '@/components/Button';
import { Dialog } from '@/components/Dialog';
import { IconButton } from '@/components/IconButton';
import { MarkdownBody } from '@/components/MarkdownBody';
import { Menu } from '@/components/Menu';
import { ProfileLink } from '@/components/profile/ProfileLink';
import { RelativeTime } from '@/components/RelativeTime';
import { TextField } from '@/components/TextField';
import { Tooltip } from '@/components/Tooltip';
import { applyRichTextPaste } from '@/lib/markdown-editing';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { CommentAttachmentList } from './CommentAttachmentList';
import { CommentReactions } from './CommentReactions';

export interface CommentItemProps {
  /**
   * ⚠ **`CommentStreamComment`, the extension — not the base `Comment`.**
   * `authorPresence` (`FE-16.2`, `FR-COLLAB-006` AC4) and `via` (`FE-70.4`,
   * `FR-AUTO-016` AC3) live only on the stream view, because
   * `activityEntrySchema.comment` hand-builds base `Comment` literals in
   * `apps/server/src/activity/hydrate.ts` and a required field there would be
   * a `tsc` error in three activity routes. Both fields are `.optional()`, so
   * a caller still holding a base `Comment` passes as it stands and renders
   * exactly as it did before this block.
   *
   * ⚠ **The producer that sentence used to name is gone, and it is stated
   * without a replacement on purpose.** `FE-30.1` made
   * `apps/web/src/lib/comments.ts` parse the two mutation read-backs with
   * `commentStreamCommentSchema` rather than the base `commentSchema`, so a
   * row reconciled from `POST /cards/:id/comments` or `PATCH /comments/:id/body`
   * now carries both fields instead of having `z.object` strip them. The claim
   * itself is unchanged: the fields stay `.optional()` on the stream schema
   * (`FE-30.1` audit §4.2 declined the drop on measurement — 25 errors in 9
   * files, 11 of them in files that chunk did not own), so a row that reaches
   * this component without them renders unmarked, **whatever produced it**.
   * Naming a second producer here would only invite the same correction again.
   */
  readonly comment: CommentStreamComment;
  readonly currentUserId?: string | undefined;
  readonly mentionMembers?: readonly BoardMemberWithHistory[];
  readonly canDelete: boolean;
  readonly onUpdate: (commentId: string, body: string) => void;
  readonly onDelete: (commentId: string) => void;
  readonly onToggleReaction?: (input: {
    readonly commentId: string;
    readonly emoji: ReactionEmoji;
    readonly reactedByMe: boolean;
  }) => void;
  readonly onCopyLink?: ((commentId: string) => void) | undefined;
  readonly onReply?: ((comment: Comment, selection?: string) => void) | undefined;
  readonly highlighted?: boolean | undefined;
  readonly newArrival?: boolean | undefined;
  readonly updateBusy?: boolean;
  readonly deleteBusy?: boolean;
  readonly reactionBusyEmoji?: ReactionEmoji | undefined;
  readonly canReact?: boolean | undefined;
  readonly canRemoveOwnReaction?: boolean | undefined;
  /**
   * SA-7 — the enclosing activity entry's `viaSuperadmin`. It rides the ENTRY,
   * not the comment: `activityEntrySchema.viaSuperadmin` is where the feed
   * projects it, and `CommentStreamComment` has no such field.
   *
   * ⚠ **It outranks `departed`.** Server `authorInAudience` is KEPT for a
   * platform admin, so one who comments on a board they hold no row on comes
   * back `authorPresence: 'departed'` — "former board member", greyed — which
   * is false: they were never a member. `deactivated` still outranks it.
   */
  readonly viaSuperadmin?: boolean | undefined;
  /**
   * The author is one of the card's members — its assignees — **now**, not at
   * the time they wrote it (owner-directed 2026-11-04: a reader of the thread
   * should see at a glance who the card is assigned to). The caller decides it
   * from the card's member list; a comment row carries no such field. A
   * deleted author (`author: null`) is never marked, whatever is passed.
   */
  readonly authorIsAssignee?: boolean | undefined;
}

const COMMENT_BODY_WARN_AT = 16_000;

export function CommentItem({
  comment,
  currentUserId,
  mentionMembers = [],
  canDelete,
  onUpdate,
  onDelete,
  onToggleReaction,
  onCopyLink,
  onReply,
  highlighted = false,
  newArrival = false,
  updateBusy = false,
  deleteBusy = false,
  reactionBusyEmoji,
  canReact = false,
  canRemoveOwnReaction = canReact,
  viaSuperadmin = false,
  authorIsAssignee = false,
}: CommentItemProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const selectedTextRef = useRef<string | undefined>(undefined);
  const editTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);
  const [editError, setEditError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const articleRef = useRef<HTMLElement | null>(null);
  const ownComment = currentUserId !== undefined && comment.author?.id === currentUserId;
  // The ternary is not redundant now the branch is keyed: `profileName(null)`
  // returns `DELETED_ACCOUNT_NAME`, the English constant, not the catalogue's
  // value. `FE-80.1` brief §6.6.
  const authorName =
    comment.author === null
      ? t('primitives.avatar.deletedAccount')
      : profileName(comment.author);
  const anchor = commentAnchor(comment.id);
  // `FE-16.2` / `FR-COLLAB-006` AC4. `??` rather than a `=== null` test: the
  // field is nullable *and* optional, and the two absences mean different
  // things on the wire (`null` = a deleted account with no person to describe;
  // `undefined` = a row parsed with the base schema, which strips it) while
  // rendering identically — no treatment, no attribute.
  const authorPresence =
    viaSuperadmin && comment.authorPresence === 'departed'
      ? undefined
      : (comment.authorPresence ?? undefined);
  const authorPresenceLabel =
    authorPresence === 'departed' || authorPresence === 'deactivated'
      ? t('cardBack.comments.item.authorPresence')
      : undefined;
  const platformAdminLabel = viaSuperadmin
    ? t('cardBack.activityEntry.platformAdminBadge')
    : undefined;
  const assigneeLabel =
    authorIsAssignee && comment.author !== null
      ? t('cardBack.comments.item.assigneeBadge')
      : undefined;
  // Either badge beside the name truncates it rather than letting it wrap —
  // SA-7's rule, which holds for any pill on this non-wrapping row.
  const nameTruncates = platformAdminLabel !== undefined || assigneeLabel !== undefined;
  const articleLabel = [
    t('cardBack.comments.item.label', { name: authorName }),
    assigneeLabel,
    authorPresenceLabel,
    platformAdminLabel,
  ]
    .filter((part): part is string => part !== undefined)
    .join(', ');
  // `FE-70.4` / `FR-AUTO-016` AC3. A blank tool name is folded into "absent"
  // rather than rendered, for the reason `renderActivitySentenceVia` throws on
  // one: ", via " is grammatical enough to read past, valid enough to
  // serialise, and wrong.
  const viaTool =
    comment.via === null || comment.via === undefined || comment.via.trim() === ''
      ? undefined
      : comment.via;
  // ⚠ Both halves of AC3 at once — *"the comment appears under the acting
  // member's name and avatar and is distinguishable from a comment they
  // typed"*. The visible marker carries the tool, the author byline beside it
  // is untouched, and this composed string is what a screen reader hears, so
  // neither half is dropped for the other.
  const viaAttribution =
    viaTool === undefined ? undefined : attributionVia(authorName, viaTool);
  const menuItems = [
    ...(ownComment
      ? [
          {
            id: 'edit',
            label: t('cardBack.comments.item.menu.edit'),
            onSelect: () => {
              setDraft(comment.body);
              setEditing(true);
            },
          },
        ]
      : []),
    ...(canDelete
      ? [
          {
            id: 'delete',
            label: t('cardBack.comments.item.menu.delete'),
            destructive: true,
            onSelect: () => setConfirmOpen(true),
          },
        ]
      : []),
  ];

  function handleEditPaste(event: ClipboardEvent<HTMLTextAreaElement>) {
    const html = event.clipboardData.getData('text/html');
    const textarea = editTextareaRef.current;
    const result = applyRichTextPaste({
      value: draft,
      html,
      selectionStart: textarea?.selectionStart ?? draft.length,
      selectionEnd: textarea?.selectionEnd ?? draft.length,
      variant: 'comment',
      maxLength: COMMENT_BODY_MAX_LENGTH,
    });

    if (!result.handled) return;
    event.preventDefault();
    setDraft(result.value);
    if (editError !== null) setEditError(null);
    requestAnimationFrame(() => {
      textarea?.focus();
      textarea?.setSelectionRange(result.selectionStart, result.selectionEnd);
    });
  }

  return (
    /*
      ⚠ `data-author-presence` appears twice, on purpose. This row-level copy is
      the assertable one — a test or a browser spec classifies the whole comment
      by it — and the avatar wrapper below repeats it because that is where the
      CSS hangs. React omits an attribute whose value is `undefined`, so a base
      `Comment` renders neither.
    */
    <article
      ref={articleRef}
      id={anchor}
      data-conversation-target={anchor}
      data-highlighted={highlighted}
      data-new-arrival={newArrival}
      data-author-presence={authorPresence}
      className={cn(
        'group scroll-mt-24 border-t border-border py-3',
        /*
          `FE-04.17`. The tint is `--color-accent-soft`, and the RING is not
          decoration — it is what carries the signal.

          Measured: in light theme the tint separates from `--color-surface` by
          1.15:1 (oklab dE 0.051), and the constrained maximum is 1.16:1, because
          `--color-ink-muted` — the timestamp and the hover icon buttons, which
          sit directly on this background — must keep 4.5:1. That is LESS than
          the 1.30:1 `border-border` hairline already drawn between every pair of
          rows, so the tint alone is a weaker signal than the rule it has to
          distinguish itself from.

          `styles.css` has met this shape once already and recorded the remedy:
          the dark modal scrim "separates nothing ... which is why FE-04.10's
          Dialog carries a mandatory 1px --color-border-strong edge". Same
          answer here. `--color-accent` measures 4.65:1 light / 4.69:1 dark on
          `--color-accent-soft`, well past 1.4.11's 3:1, and it matches the
          `border-accent` the reaction chip and template badge already wear over
          the same ground.

          Each utility carries its own variant prefix rather than sharing one,
          for the reason spelled out on the avatar wrapper below: `tailwind-merge`
          keys on the full class, so two differently-keyed utilities both
          survive `cn` and neither arm silently evicts the other.
        */
        'data-[highlighted=true]:rounded-card data-[highlighted=true]:bg-accent-soft data-[highlighted=true]:my-2 data-[highlighted=true]:px-2 data-[highlighted=true]:ring-1 data-[highlighted=true]:ring-accent',
        'data-[new-arrival=true]:rounded-card data-[new-arrival=true]:bg-accent-soft data-[new-arrival=true]:my-2 data-[new-arrival=true]:px-2 data-[new-arrival=true]:ring-1 data-[new-arrival=true]:ring-accent',
      )}
      aria-label={articleLabel}
    >
      <div className="flex items-start gap-3">
        {comment.author === null ? (
          <Avatar profile={null} state="unknown" size="sm" decorative />
        ) : (
          /*
            `FR-COLLAB-006` AC4 — *"their name still renders with a
            deactivated-style avatar and the comment is not removed"*.

            ⚠ A WRAPPER, NOT A PROP. `Avatar`'s `AvatarState` is
            `'normal' | 'unknown' | 'none'` and its prop union forbids a real
            profile with a non-normal state, so the treatment cannot be asked
            for through the primitive — and `Avatar.tsx` is another block's
            file. A wrapper reaches the same rendering with no cross-block edit.

            ⚠ THE CLASS LIST IS STATIC AND THE `data-` ATTRIBUTE IS WHAT
            VARIES. Both variants are spelled out rather than sharing one
            selector so that `tailwind-merge` sees two differently-keyed
            utilities and drops neither; the sibling test asserts both survive
            `cn`, which is the only way that failure is visible before it ships.
            `departed` and `deactivated` render identically on purpose (D-8):
            AC4 asks for one treatment, and the precedence between the two
            states is the server's to decide, not this component's.
          */
          <span
            data-author-presence={authorPresence}
            className={cn(
              'inline-flex shrink-0 rounded-full',
              'data-[author-presence=departed]:opacity-60 data-[author-presence=departed]:grayscale',
              'data-[author-presence=deactivated]:opacity-60 data-[author-presence=deactivated]:grayscale',
            )}
          >
            <Avatar profile={comment.author} size="sm" decorative />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-sm">
            {/*
              SA-7 — "it must not push the actor name onto a second line". This
              row does not wrap, so the badge cannot be parted from the name;
              the name itself can still break inside its link, because a flex
              item will not shrink below its longest word. Under the flag it
              truncates instead, as `ActivityEntryItem`'s does; an ordinary
              byline is left exactly as it was.
            */}
            {comment.author === null ? (
              <span
                className={cn(
                  'font-medium text-ink',
                  nameTruncates ? 'min-w-0 truncate' : undefined,
                )}
              >
                {authorName}
              </span>
            ) : (
              <ProfileLink
                profile={comment.author}
                className={nameTruncates ? 'min-w-0 truncate' : undefined}
              />
            )}
            {assigneeLabel === undefined ? null : (
              /*
                The card's assignee. Accent rather than the neutral grey of the
                other byline pills, because it is the one a reader scans for;
                `--color-accent` on `--color-accent-soft` measures 4.65:1 light /
                4.69:1 dark (see the highlight note above), past AA for text.
              */
              <span
                data-comment-author-assignee=""
                className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-accent/35 bg-accent-soft px-2 py-0.5 text-xs font-medium text-accent"
              >
                <UserCheck aria-hidden="true" className="size-3 shrink-0" />
                {assigneeLabel}
              </span>
            )}
            {authorPresenceLabel === undefined ? null : (
              <span
                data-author-presence-label=""
                className="inline-flex max-w-40 shrink-0 rounded-full border border-border bg-surface px-2 py-0.5 text-xs font-medium text-ink-muted"
              >
                <span className="truncate">{authorPresenceLabel}</span>
              </span>
            )}
            {platformAdminLabel === undefined ? null : (
              <span className="shrink-0 whitespace-nowrap rounded-full border border-border bg-surface px-2 py-0.5 text-xs font-medium text-ink-muted">
                {platformAdminLabel}
              </span>
            )}
            {viaAttribution === undefined ? null : (
              /*
                `FR-AUTO-016` AC3 / `FE-70.4`. The visible half is the tool's
                own name beside the connected-tool icon this app already uses
                for a power-up; the spoken half is the composed attribution, so
                a screen reader hears the member AND the tool rather than a
                bare product name hanging off the byline. The `aria-hidden` /
                `sr-only` pair is `Avatar`'s idiom for exactly this split.

                ⚠ No new interface string is added here: the visible text is
                the tool's name, which is data, and the spoken text is
                `ACTIVITY_VIA_TEMPLATE`, which `@molt/shared` already owns and
                already translates.

                ⚠ THE VISIBLE HALF IS BOUNDED AND THE SPOKEN HALF IS NOT, on
                purpose. `via` is a bare `z.string()` over a `varchar(255)`
                column, and this byline is a `flex` row that also holds the
                timestamp, the `(edited)` marker and the hover actions — an
                unbounded name would push them off a narrow card back. jsdom
                has no layout, so no unit test can see that; the bound is
                written rather than measured. `truncate` clips only the
                `aria-hidden` copy, so a screen reader still hears the whole
                name.
              */
              <span
                data-comment-via={viaTool}
                className="inline-flex max-w-40 shrink-0 items-center gap-1 rounded-full border border-border bg-surface px-2 py-0.5 text-xs font-medium text-ink-muted"
              >
                <PlugZap aria-hidden="true" className="size-3 shrink-0" />
                <span aria-hidden="true" className="truncate">
                  {viaTool}
                </span>
                <span className="sr-only">{viaAttribution}</span>
              </span>
            )}
            {/*
              `comments.number` — the comment's number on its card, the `#3` a
              person cites and the `<n>` in `/c/<ref>/com/<n>` (owner-directed
              2026-10-31, `docs/notes/comment-numbers.md`). `shrink-0` keeps it
              out of the name's truncation. The `> 0` guard is belt and braces:
              the schema says positive and no client path builds a comment
              view, but a `#0` would be a lie, so none renders.
            */}
            {comment.number > 0 ? (
              <span
                data-comment-number={comment.number}
                className="shrink-0 tabular-nums text-ink-muted"
              >
                {t('cardBack.comments.item.number', { number: comment.number })}
              </span>
            ) : null}
            <RelativeTime value={comment.createdAt} className="text-ink-muted" />
            {comment.editedAt !== null ? (
              <span className="text-ink-muted">{t('cardBack.comments.item.edited')}</span>
            ) : null}
            {newArrival ? (
              <span className="rounded-full border border-accent/35 bg-surface px-2 py-0.5 text-xs font-semibold text-accent">
                {t('cardBack.comments.item.newBadge')}
              </span>
            ) : null}
            <div className="ml-auto flex items-center gap-1 opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
              {onCopyLink === undefined ? null : (
                <Tooltip content={t('cardBack.comments.item.copyLinkTooltip')}>
                  <IconButton
                    label={t('cardBack.comments.item.copyLink')}
                    size="sm"
                    onClick={() => onCopyLink(comment.id)}
                  >
                    <Link2 aria-hidden="true" className="size-4" />
                  </IconButton>
                </Tooltip>
              )}
              {onReply === undefined ? null : (
                <Tooltip content={t('cardBack.comments.item.replyTooltip')}>
                  <IconButton
                    label={t('cardBack.comments.item.reply', { name: authorName })}
                    size="sm"
                    onPointerDown={() => {
                      selectedTextRef.current = selectedTextWithin(articleRef.current);
                    }}
                    onClick={() => {
                      onReply(
                        comment,
                        selectedTextRef.current ?? selectedTextWithin(articleRef.current),
                      );
                      selectedTextRef.current = undefined;
                    }}
                  >
                    <MessageSquareQuote aria-hidden="true" className="size-4" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </div>
          {editing ? (
            <div className="mt-2 flex flex-col gap-2">
              <TextField
                ref={editTextareaRef}
                label={t('cardBack.comments.item.editorLabel')}
                multiline
                value={draft}
                onPaste={handleEditPaste}
                onChange={(event) => {
                  setDraft(event.currentTarget.value);
                  if (editError !== null) setEditError(null);
                }}
                {...(editError === null ? {} : { error: editError })}
                counter={{
                  value: draft.trim().length,
                  max: COMMENT_BODY_MAX_LENGTH,
                  warnAt: COMMENT_BODY_WARN_AT,
                }}
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="primary"
                  busy={updateBusy}
                  onClick={() => {
                    const trimmed = draft.trim();
                    if (trimmed.length === 0) {
                      setEditError(t('cardBack.comments.error.empty'));
                      return;
                    }
                    if (trimmed.length > COMMENT_BODY_MAX_LENGTH) {
                      setEditError(t('cardBack.comments.error.tooLong'));
                      return;
                    }
                    onUpdate(comment.id, trimmed);
                    setEditing(false);
                  }}
                >
                  {t('cardBack.comments.item.save')}
                </Button>
                <Button
                  size="sm"
                  variant="quiet"
                  onClick={() => {
                    setEditing(false);
                    setDraft(comment.body);
                    setEditError(null);
                  }}
                >
                  {t('cardBack.comments.item.editCancel')}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <MarkdownBody
                markdown={comment.body}
                variant="comment"
                mentionMembers={mentionMembers}
                currentUserId={currentUserId}
                clamp={{
                  maxHeight: 180,
                  moreLabel: t('cardBack.comments.item.showMore'),
                }}
              />
              <CommentAttachmentList attachments={comment.attachments} />
              <CommentReactions
                commentId={comment.id}
                reactions={comment.reactions}
                canReact={canReact && onToggleReaction !== undefined}
                canRemoveOwnReaction={
                  canRemoveOwnReaction && onToggleReaction !== undefined
                }
                busy={reactionBusyEmoji !== undefined}
                onAdd={(commentId, emoji) =>
                  onToggleReaction?.({
                    commentId,
                    emoji,
                    reactedByMe: false,
                  })
                }
                onRemove={(commentId, emoji) =>
                  onToggleReaction?.({
                    commentId,
                    emoji,
                    reactedByMe: true,
                  })
                }
              />
            </>
          )}
        </div>
        {menuItems.length > 0 ? (
          <Menu
            align="end"
            trigger={
              <IconButton
                label={
                  ownComment
                    ? t('cardBack.comments.item.actionsOwn')
                    : t('cardBack.comments.item.actionsOther', { name: authorName })
                }
                size="sm"
              >
                <MoreHorizontal aria-hidden="true" className="size-4" />
              </IconButton>
            }
            items={menuItems}
          />
        ) : null}
      </div>

      <Dialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        size="sm"
        title={t('cardBack.comments.item.delete.title')}
        description={t('cardBack.comments.item.delete.description')}
        closeControl={
          <IconButton label={t('cardBack.comments.item.delete.close')} size="sm">
            <X aria-hidden="true" className="size-4" />
          </IconButton>
        }
        footer={
          <>
            <Button size="sm" variant="quiet" onClick={() => setConfirmOpen(false)}>
              {t('cardBack.comments.item.delete.cancel')}
            </Button>
            <Button
              size="sm"
              variant="primary"
              busy={deleteBusy}
              onClick={() => {
                onDelete(comment.id);
                setConfirmOpen(false);
              }}
            >
              {t('cardBack.comments.item.delete.confirm')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-muted">
          {t('cardBack.comments.item.delete.body')}
        </p>
      </Dialog>
    </article>
  );
}

/**
 * `{author}, via {tool}` — the spoken attribution for a comment an automation
 * posted on a member's behalf, composed from `@molt/shared`'s
 * `ACTIVITY_VIA_TEMPLATE`.
 *
 * ⚠ **The client composes it, and that is `FR-COLLAB-018`'s edge case rather
 * than a preference** — the server sends `author` and `via` separately and
 * concatenates no sentence, precisely so the join happens where it can be
 * translated.
 *
 * ⚠ **ONE pass over the template, through a callback, and both halves matter**
 * — the reasoning is `renderActivitySentenceVia`'s, transplanted because that
 * helper takes an activity entry's `templateKey` and `tokens` and a comment has
 * neither. A callback replacement is inserted verbatim, so a display name
 * containing `$&` or `$'` cannot reach `String.replace`'s pattern syntax; and
 * one pass means the substituted name is never rescanned, so a member calling
 * themselves `{tool}` cannot become the tool's name. Two chained
 * `.replace('{sentence}', …)` calls fail both ways, silently, on data a member
 * chose.
 *
 * It throws on a slot the template grew and this function does not know, which
 * is unreachable while `ACTIVITY_VIA_TEMPLATE` is the two slots it declares —
 * loud rather than silent if a third arrives.
 */
function attributionVia(authorName: string, automationName: string): string {
  return ACTIVITY_VIA_TEMPLATE.replace(
    /\{([A-Za-z][A-Za-z0-9]*)\}/g,
    (_match, slot: string) => {
      if (slot === 'sentence') return authorName;
      if (slot === 'tool') return automationName;
      throw new Error(`comment via template needs a token for {${slot}}`);
    },
  );
}

function selectedTextWithin(root: HTMLElement | null): string | undefined {
  if (root === null || typeof window === 'undefined') return undefined;
  const selection = window.getSelection();
  if (selection === null || selection.isCollapsed) return undefined;
  const range = selection.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return undefined;
  const text = selection.toString().trim();
  return text.length === 0 ? undefined : text;
}
